import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isCommunitySponsor } from '../../config/r2rCommercialSeller';
import { ensureUser } from '../../util/data';
import { profileContactUsername, xenforoDirectMessageUrl } from '../../util/xenforoUrls';

import { Avatar, ExternalLink, IconReviewStar, NamedLink } from '../../components';
import CommunitySponsorBadge from '../ProfilePage/CommunitySponsorBadge';

import css from './SectionSellerLine.module.css';

/**
 * Compact seller identity under the listing title.
 * Shows Community Sponsor, rating, sales, and Contact (XenForo DM) when present.
 *
 * @component
 */
const SectionSellerLine = props => {
  const { author, isOwnListing = false, className } = props;
  const intl = useIntl();
  const user = ensureUser(author);
  const userId = user?.id?.uuid;

  if (!userId) {
    return null;
  }

  const profile = user.attributes?.profile || {};
  const displayName = profile.displayName || '';
  const metadata = profile.metadata || {};
  const reviewAverageRaw = metadata.reviewAverage;
  const reviewCountRaw = metadata.reviewCount;
  const reviewCount = Number.isFinite(reviewCountRaw) ? reviewCountRaw : 0;
  const reviewAverage = Number.isFinite(reviewAverageRaw) ? reviewAverageRaw : null;
  const showRating = reviewCount > 0 && reviewAverage != null;
  const itemsSoldCount =
    typeof metadata.itemsSoldCount === 'number' ? metadata.itemsSoldCount : null;
  const showSales = itemsSoldCount != null && itemsSoldCount > 0;
  const showSponsor = isCommunitySponsor(user);

  const contactUsername = !isOwnListing
    ? profileContactUsername({
        publicData: profile.publicData,
        displayName: profile.displayName,
      })
    : null;
  const contactHref = contactUsername ? xenforoDirectMessageUrl(contactUsername) : null;

  const ratingAverageFormatted = showRating
    ? intl.formatNumber(reviewAverage, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;

  return (
    <div className={classNames(css.root, className)}>
      <Avatar className={css.avatar} user={user} />
      <NamedLink className={css.name} name="ProfilePage" params={{ id: userId }}>
        {displayName}
      </NamedLink>
      {showSponsor ? <CommunitySponsorBadge className={css.sponsorBadge} size="compact" /> : null}
      {(showRating || showSales) && (
        <span className={css.meta}>
          {showSales ? (
            <span>
              <FormattedMessage
                id="ListingPage.sellerSalesCount"
                values={{ count: itemsSoldCount }}
              />
            </span>
          ) : null}
          {showSales && showRating ? <span className={css.metaSep}>·</span> : null}
          {showRating ? (
            <span className={css.rating} aria-hidden="true">
              <span>{ratingAverageFormatted}</span>
              <IconReviewStar className={css.ratingStar} isFilled isAriaHidden />
            </span>
          ) : null}
        </span>
      )}
      {contactHref ? (
        <ExternalLink className={css.contactLink} href={contactHref}>
          <FormattedMessage id="ListingPage.contactUser" />
        </ExternalLink>
      ) : null}
    </div>
  );
};

export default SectionSellerLine;
